import { action, observable } from 'mobx'
import { task } from 'mobx-task'
import { func, validationContext } from 'validx'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { CursorPageQueryParams } from '@/infra/api-client'
import type { CursorPaged } from '@/infra/paging'
import { type Id } from '@/infra/id'
import type { ListWorkspacesFn, PlatformAdminWorkspaceFn } from '@/features/iam'

export class JoinWorkspaceAsAdminDialogViewModel {
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * The currently selected workspace.
   */
  @observable
  accessor workspace: Workspace | null = null

  /**
   * Validation rules for the join workspace form.
   */
  validation = validationContext(this, {
    workspace: [
      func(() => this.workspace !== null, 'You must select a workspace.'),
    ],
  })

  constructor(
    private readonly _getUserMembershipWorkspaceIds: () => Id[],
    private readonly _onJoined: () => Promise<void>,
    private readonly _listWorkspaces: ListWorkspacesFn,
    private readonly _joinWorkspaceAsAdmin: PlatformAdminWorkspaceFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Searches for workspaces.
   */
  searchWorkspaces = async (
    searchText: string,
    pagination: CursorPageQueryParams,
  ): Promise<CursorPaged<Workspace>> => {
    const page = await this._listWorkspaces({
      after: pagination.after ?? null,
      limit: pagination.limit ?? 10,
      searchText,
    })
    const excludeWorkspaceIds = this._getUserMembershipWorkspaceIds()

    return {
      data: page.data.filter(
        (ws) =>
          !excludeWorkspaceIds.includes(ws.id) &&
          !ws.name.toLowerCase().includes('e2e') &&
          !ws.slug.toLowerCase().includes('e2e'),
      ),
      next: page.next,
    }
  }

  /**
   * Set the currently selected workspace.
   */
  @action.bound
  setWorkspace(workspace?: Workspace | null) {
    this.workspace = workspace ?? null
  }

  /**
   * Join the selected workspace as admin.
   */
  submit = task.resolved(async () => {
    const workspace = this.workspace
    if (!this.validation.reset().validate().isValid || !workspace) {
      return
    }

    await this._joinWorkspaceAsAdmin(workspace.id).catch(
      this._createAlertErrorHandler('Joining the workspace failed'),
    )

    // The join succeeded, so a failed refresh still reports success.
    await this._onJoined().catch(() => undefined)

    this._newAlert()
      .withState('success')
      .saying('Success!', `Joined ${workspace.name} as admin.`)
      .autoDismiss()
      .show()

    this.modal.close()
  })

  @action.bound
  reset() {
    this.workspace = null
    this.validation.reset()
  }
}
